// components/NotificationBell.jsx
import React, { useState, useEffect } from 'react';
import {
  Badge,
  Dropdown,
  List,
  Avatar,
  Button,
  Typography,
  Tag,
  Divider,
  Empty,
  notification,
  Spin,
} from 'antd';
import {
  BellOutlined,
  ShoppingCartOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  MessageOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';

const { Text, Title } = Typography;

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  // ✅ Only fetch notifications on manual refresh or hard reload
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/notifications');
      if (response.data.success) {
        setNotifications(response.data.notifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      notification.error({
        message: 'Failed to load notifications',
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Manual refresh button handler
  const handleRefresh = () => {
    fetchNotifications();
  };

  const markAsRead = async (notificationId) => {
    try {
      await axiosInstance.post(`/admin/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
      notification.error({
        message: 'Failed to mark as read',
        placement: 'topRight',
      });
    }
  };

  const markAllAsRead = async () => {
    try {
      await axiosInstance.post('/admin/notifications/read-all');
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, isRead: true }))
      );
      notification.success({
        message: 'All notifications marked as read',
        placement: 'topRight',
      });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      notification.error({
        message: 'Failed to mark all as read',
        placement: 'topRight',
      });
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    if (notification.navigateTo) {
      navigate(notification.navigateTo);
    }
    setVisible(false);
  };

  const getNotificationIcon = (type, priority) => {
    const color = getPriorityColor(priority);
    switch (type) {
      case 'new_order':
        return <ShoppingCartOutlined style={{ color, fontSize: '16px' }} />;
      case 'low_stock':
        return <ExclamationCircleOutlined style={{ color, fontSize: '16px' }} />;
      case 'production_update':
        return <CheckCircleOutlined style={{ color, fontSize: '16px' }} />;
      case 'delivery_scheduled':
        return <ClockCircleOutlined style={{ color, fontSize: '16px' }} />;
      case 'system_alert':
        return <InfoCircleOutlined style={{ color, fontSize: '16px' }} />;
      default:
        return <MessageOutlined style={{ color, fontSize: '16px' }} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return '#ef4444';
      case 'high':
        return '#f97316';
      case 'medium':
        return '#eab308';
      case 'low':
        return '#22c55e';
      default:
        return '#3b82f6';
    }
  };

  const getPriorityTag = (priority) => {
    const colorMap = {
      urgent: 'red',
      high: 'orange',
      medium: 'gold',
      low: 'green',
    };
    return (
      <Tag 
        color={colorMap[priority] || 'blue'} 
        className="text-xs px-2 py-0 h-5 border-none capitalize"
      >
        {priority}
      </Tag>
    );
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diff = now - notificationTime;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const unreadCount = notifications.filter(notif => !notif.isRead).length;

  const notificationContent = (
    <div className="w-96 max-h-[500px] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Title level={5} className="!mb-0 !text-white">
              Notifications
            </Title>
            {unreadCount > 0 && (
              <Badge 
                count={unreadCount} 
                className="bg-red-500 text-white font-semibold"
              />
            )}
          </div>
          
          <div className="flex gap-2">
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              className="!text-white/80 hover:!text-white hover:!bg-white/10 !border-none"
              size="small"
              loading={loading}
            />
            {unreadCount > 0 && (
              <Button
                type="text"
                onClick={markAllAsRead}
                className="!text-white/80 hover:!text-white hover:!bg-white/10 !border-none text-xs"
                size="small"
              >
                Mark all
              </Button>
            )}
          </div>
        </div>
      </div>

      <Divider className="!my-0" />

      {/* Notifications List */}
      <div className="max-h-[360px] overflow-y-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <Spin size="large" />
            <Text type="secondary">Loading notifications...</Text>
          </div>
        ) : notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications"
            className="py-10"
          />
        ) : (
          <List
            dataSource={notifications}
            renderItem={(item) => (
              <List.Item
                className={`!p-0 !border-b !border-gray-100 cursor-pointer transition-all duration-200 ${
                  !item.isRead 
                    ? 'bg-blue-50 border-l-2 border-l-blue-400 hover:bg-blue-100' 
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => handleNotificationClick(item)}
              >
                <div className="flex gap-3 p-4 w-full">
                  <div className="flex-shrink-0">
                    <Badge dot={!item.isRead} offset={[-2, 2]}>
                      <Avatar
                        size={40}
                        icon={getNotificationIcon(item.type, item.priority)}
                        className="flex items-center justify-center border-2 border-gray-300 bg-gray-100"
                        style={{ color: getPriorityColor(item.priority) }}
                      />
                    </Badge>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <Text strong className="text-sm text-gray-900 line-clamp-1">
                        {item.title}
                      </Text>
                      {getPriorityTag(item.priority)}
                    </div>
                    
                    <Text className="text-xs text-gray-600 leading-relaxed block mb-2">
                      {item.message}
                    </Text>
                    
                    <div className="flex items-center justify-between">
                      <Text type="secondary" className="text-xs text-gray-500">
                        {formatTime(item.createdAt)}
                      </Text>
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </div>

      {notifications.length > 0 && (
        <>
          <Divider className="!my-0" />
          <div className="bg-gray-50 px-5 py-3 text-center">
            <Button 
              type="link" 
              onClick={() => {
                navigate('/notifications');
                setVisible(false);
              }}
              icon={<EyeOutlined />}
              className="!text-blue-600 hover:!text-blue-700 font-medium"
            >
              View All Notifications
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Dropdown
      overlay={notificationContent}
      trigger={['click']}
      open={visible}
      onOpenChange={setVisible}
      placement="bottomRight"
      overlayStyle={{ 
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        borderRadius: '12px'
      }}
    >
      <div className="relative">
        <Badge count={unreadCount} size="small" offset={[-4, 4]}>
          <Button
            type="text"
            className={`
              !w-10 !h-10 !rounded-full !border-none !flex !items-center !justify-center
              transition-all duration-200
              ${visible 
                ? '!text-blue-600 !bg-blue-50 shadow-inner' 
                : unreadCount > 0 
                  ? '!text-blue-600 hover:!bg-blue-50' 
                  : '!text-gray-600 hover:!bg-gray-100'
              }
            `}
            icon={<BellOutlined className="text-lg" />}
            shape="circle"
            size="large"
          />
        </Badge>
      </div>
    </Dropdown>
  );
};

export default NotificationBell;
